import React, { useEffect, useState, useMemo } from 'react';
import Styles from './PublishSettingsModal.module.scss';
import { FiX, FiCalendar, FiChevronDown, FiPlus, FiCheck } from 'react-icons/fi';
import Datepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getUserIntegrations, getIntegrationInfo, publishDocuments } from 'services/integrationService';
import { saveDocumentSettings } from 'services/documentService';
import { useParams, useNavigate } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { PlatformPreviewSwitcher } from 'Components/PlatformPreviews';
import usePlatformValidation from 'hooks/usePlatformValidation';

// Platform type constants
const SOCIAL_PLATFORMS = ['linkedin', 'facebook', 'x', 'twitter', 'instagram'];
const BLOG_PLATFORMS = ['wordpress', 'wix', 'webflow'];

const isSocialPlatform = (type) => SOCIAL_PLATFORMS.includes(type?.toLowerCase());
const isBlogPlatform = (type) => BLOG_PLATFORMS.includes(type?.toLowerCase());

const PublishSettingsModal = ({
  isOpen,
  onClose,
  document,
  onPublishSuccess,
  refetchContent
}) => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [integrations, setIntegrations] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [publishImmediately, setPublishImmediately] = useState(true);
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [scheduledTime, setScheduledTime] = useState('14:30');
  const [coverImage, setCoverImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Blog settings state
  const [blogSettings, setBlogSettings] = useState({
    author: '',
    category: '',
    tags: [],
    slug: '',
    metaDescription: '',
    status: 'publish'
  });

  // Fetched options for blog platforms
  const [blogOptions, setBlogOptions] = useState({
    authors: [],
    categories: [],
    tags: [],
    isLoading: false
  });

  // Extract hashtags from document content
  const extractedHashtags = useMemo(() => {
    const content = document?.content || '';
    const hashtagRegex = /#\w+/g;
    const matches = content.match(hashtagRegex) || [];
    return matches.slice(0, 10);
  }, [document?.content]);

  // Platform validation
  const validation = usePlatformValidation(
    document?.content || '',
    selectedChannel?.type,
    extractedHashtags
  );

  // Generate slug from document title
  useEffect(() => {
    if (document?.title && isBlogPlatform(selectedChannel?.type)) {
      const generatedSlug = document.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 100);
      setBlogSettings(prev => ({ ...prev, slug: generatedSlug }));
    }
  }, [document?.title, selectedChannel?.type]);

  useEffect(() => {
    if (isOpen) {
      fetchUserIntegrations();
    }
  }, [isOpen]);

  // Fetch blog platform options when a blog platform is selected
  useEffect(() => {
    if (selectedChannel && isBlogPlatform(selectedChannel.type)) {
      fetchBlogOptions(selectedChannel.id);
    }
  }, [selectedChannel]);

  const fetchUserIntegrations = async () => {
    try {
      const { data } = await getUserIntegrations();
      const activeIntegrations = data?.platform_integrations?.filter(
        (int) => int.is_active
      ) || [];
      setIntegrations(activeIntegrations);
      if (activeIntegrations.length > 0) {
        setSelectedChannel(activeIntegrations[0]);
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const fetchBlogOptions = async (integrationId) => {
    setBlogOptions(prev => ({ ...prev, isLoading: true }));
    try {
      const { data } = await getIntegrationInfo(integrationId);
      setBlogOptions({
        authors: data?.authors || [],
        categories: data?.categories || [],
        tags: data?.tags || [],
        isLoading: false
      });
    } catch (error) {
      console.error('Error fetching blog options:', error);
      setBlogOptions(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);

      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setCoverImage(null);
    setPreviewUrl(null);
  };

  const handlePublish = async () => {
    if (!selectedChannel) {
      toast.error('Please select a channel to publish');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        integration_id: selectedChannel.id,
        story_detail: {
          cover_image: coverImage,
        },
        status: isBlogPlatform(selectedChannel.type) ? blogSettings.status : 'publish',
        scheduled_at: publishImmediately ? null : combineDateAndTime(),
      };

      // Add blog-specific settings
      if (isBlogPlatform(selectedChannel.type)) {
        payload.story_detail.author = blogSettings.author;
        payload.story_detail.category = blogSettings.category;
        payload.story_detail.tags = blogSettings.tags;
        payload.seo_setting = {
          slug: blogSettings.slug,
          meta_description: blogSettings.metaDescription
        };
      }

      // Enable thread mode for X/Twitter if content is multi-tweet
      if (selectedChannel.type === 'x') {
        payload.x_thread = true;
      }

      // Step 1: Save settings (including cover image) to database
      await saveDocumentSettings(documentId, payload);

      // Step 2: Actually publish to the platform
      const publishPayload = {
        integration_id: selectedChannel.id,
      };

      // Add thread option for X/Twitter
      if (selectedChannel.type === 'x') {
        publishPayload.x_thread = true;
      }

      const publishResponse = await publishDocuments(documentId, publishPayload);

      if (publishResponse?.data) {
        toast.success(publishResponse.data.message || 'Content published successfully!');
        if (refetchContent) {
          refetchContent(false);
        }
        if (onPublishSuccess) {
          onPublishSuccess(publishResponse.data);
        }
        onClose();
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error(error?.response?.data?.error || 'Failed to publish');
    } finally {
      setIsLoading(false);
    }
  };

  const combineDateAndTime = () => {
    const [hours, minutes] = scheduledTime.split(':');
    const combined = new Date(scheduledDate);
    combined.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return combined.toISOString();
  };

  const handleTagToggle = (tagId) => {
    setBlogSettings(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(t => t !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  if (!isOpen) return null;

  const showPreview = isSocialPlatform(selectedChannel?.type);
  const showBlogSettings = isBlogPlatform(selectedChannel?.type);

  return (
    <div className={Styles.overlay} onClick={onClose}>
      <div className={Styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={Styles.header}>
          <h2 className={Styles.title}>Settings</h2>
          <button className={Styles.closeBtn} onClick={onClose}>
            <FiX size={16} />
          </button>
        </div>

        {/* Content */}
        <div className={Styles.content}>
          {/* Left Panel - Settings Form */}
          <div className={Styles.leftPanel}>
            {/* Choose Channel */}
            <div className={Styles.section}>
              <h3 className={Styles.sectionTitle}>Choose Channel</h3>
              <div className={Styles.channelList}>
                {integrations.map((integration) => (
                  <ChannelCard
                    key={integration.id}
                    integration={integration}
                    isSelected={selectedChannel?.id === integration.id}
                    onClick={() => setSelectedChannel(integration)}
                  />
                ))}
                {integrations.length === 0 && (
                  <div className={Styles.emptyState}>
                    <div className={Styles.integrationLogos}>
                      <img src="/Images/LinkedInLogo.svg" alt="LinkedIn" />
                      <img src="/Images/FacebookLogo.svg" alt="Facebook" />
                      <img src="/Images/wordpress.svg" alt="WordPress" />
                      <img src="/Images/logo-x.png" alt="X" />
                    </div>
                    <p className={Styles.emptyStateText}>
                      No channels connected
                    </p>
                    <p className={Styles.emptyStateSubtext}>
                      Connect your accounts to start publishing content
                    </p>
                    <button
                      className={Styles.connectBtn}
                      onClick={() => {
                        onClose();
                        navigate(ROUTES.Integration);
                      }}
                    >
                      <FiPlus size={16} />
                      Connect Channel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Date & Time */}
            <div className={Styles.section}>
              <h3 className={Styles.sectionTitle}>Date & Time</h3>

              {/* Toggle */}
              <div className={Styles.toggleRow}>
                <label className={Styles.toggle}>
                  <input
                    type="checkbox"
                    checked={publishImmediately}
                    onChange={() => setPublishImmediately(!publishImmediately)}
                  />
                  <span className={Styles.toggleSlider}></span>
                </label>
                <span className={Styles.toggleLabel}>Publish immediately</span>
              </div>

              {/* Date/Time Pickers */}
              {!publishImmediately && (
                <div className={Styles.dateTimeRow}>
                  <div className={Styles.dateField}>
                    <label>Date</label>
                    <div className={Styles.dateInput}>
                      <Datepicker
                        selected={scheduledDate}
                        onChange={(date) => setScheduledDate(date)}
                        dateFormat="MM/dd/yyyy"
                        minDate={new Date()}
                        className={Styles.datepicker}
                      />
                      <FiCalendar className={Styles.dateIcon} />
                    </div>
                  </div>
                  <div className={Styles.timeField}>
                    <label>Time</label>
                    <div className={Styles.timeInput}>
                      <select
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                      >
                        {generateTimeOptions().map((time) => (
                          <option key={time} value={time}>
                            {formatTime(time)}
                          </option>
                        ))}
                      </select>
                      <FiChevronDown className={Styles.timeIcon} />
                    </div>
                  </div>
                </div>
              )}

              {/* Recommended Time */}
              {!publishImmediately && (
                <div className={Styles.recommended}>
                  <span className={Styles.recommendedLabel}>Recommended</span>
                  <p className={Styles.recommendedText}>
                    Best time for your audience: Tuesday 2:30 PM
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Preview OR Blog Settings */}
          <div className={Styles.rightPanel}>
            {showPreview && (
              <div className={Styles.previewWrapper}>
                <h3 className={Styles.previewTitle}>Preview</h3>
                <PlatformPreviewSwitcher
                  platformType={selectedChannel?.type}
                  user={user}
                  content={document?.content || ''}
                  hashtags={extractedHashtags}
                  imageUrl={previewUrl}
                  onRemoveImage={removeImage}
                  contentType={document?.content_type}
                />
                {/* Image Upload */}
                {!previewUrl && (
                  <label className={Styles.uploadZone}>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleImageUpload}
                      hidden
                    />
                    <FiPlus size={24} className={Styles.uploadIcon} />
                    <p className={Styles.uploadText}>Add cover image</p>
                    <p className={Styles.uploadHint}>(recommended for better engagement)</p>
                  </label>
                )}
                {/* Validation Warning */}
                {validation.message && (
                  <div className={`${Styles.validationMessage} ${Styles[validation.status]}`}>
                    {validation.message}
                  </div>
                )}
              </div>
            )}

            {showBlogSettings && (
              <BlogSettingsPanel
                platformType={selectedChannel?.type}
                settings={blogSettings}
                onSettingsChange={setBlogSettings}
                options={blogOptions}
                coverImage={previewUrl}
                onImageUpload={handleImageUpload}
                onRemoveImage={removeImage}
                onTagToggle={handleTagToggle}
              />
            )}

            {!showPreview && !showBlogSettings && selectedChannel && (
              <div className={Styles.previewWrapper}>
                <p className={Styles.emptyStateText}>Select a channel to configure settings</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={Styles.footer}>
          <button className={Styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            className={Styles.publishBtn}
            onClick={handlePublish}
            disabled={isLoading || !selectedChannel || (showPreview && !validation.isValid) || (selectedChannel?.type?.toLowerCase() === 'wordpress' && !blogSettings.author)}
          >
            {isLoading ? 'Publishing...' : validation.threadCount > 1 ? `Publish as Thread (${validation.threadCount})` : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Blog Settings Panel Component
const BlogSettingsPanel = ({
  platformType,
  settings,
  onSettingsChange,
  options,
  coverImage,
  onImageUpload,
  onRemoveImage,
  onTagToggle
}) => {
  const isWordpress = platformType?.toLowerCase() === 'wordpress';
  const isWix = platformType?.toLowerCase() === 'wix';
  const isWebflow = platformType?.toLowerCase() === 'webflow';

  const handleChange = (field, value) => {
    onSettingsChange(prev => ({ ...prev, [field]: value }));
  };

  const platformName = platformType?.charAt(0).toUpperCase() + platformType?.slice(1);

  return (
    <div className={Styles.blogSettingsPanel}>
      <h3 className={Styles.previewTitle}>{platformName} Settings</h3>

      {options.isLoading ? (
        <div className={Styles.loadingState}>Loading settings...</div>
      ) : (
        <div className={Styles.blogSettingsForm}>
          {/* Cover Image */}
          <div className={Styles.formGroup}>
            <label className={Styles.formLabel}>Cover Image</label>
            {coverImage ? (
              <div className={Styles.imagePreviewSmall}>
                <img src={coverImage} alt="Cover" />
                <button className={Styles.removeImageBtn} onClick={onRemoveImage}>
                  <FiX size={14} />
                </button>
              </div>
            ) : (
              <label className={Styles.uploadZoneSmall}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onImageUpload}
                  hidden
                />
                <FiPlus size={18} />
                <span>Upload image</span>
              </label>
            )}
          </div>

          {/* Author - WordPress only (required) */}
          {isWordpress && options.authors.length > 0 && (
            <div className={Styles.formGroup}>
              <label className={Styles.formLabel}>Author <span className={Styles.required}>*</span></label>
              <select
                className={`${Styles.formSelect} ${!settings.author ? Styles.selectError : ''}`}
                value={settings.author}
                onChange={(e) => handleChange('author', e.target.value)}
              >
                <option value="">Select author</option>
                {options.authors.map((author) => (
                  <option key={author.id} value={author.id}>
                    {author.name}
                  </option>
                ))}
              </select>
              {!settings.author && <span className={Styles.errorText}>Author is required</span>}
            </div>
          )}

          {/* Category - WordPress and Wix */}
          {(isWordpress || isWix) && options.categories.length > 0 && (
            <div className={Styles.formGroup}>
              <label className={Styles.formLabel}>Category</label>
              <select
                className={Styles.formSelect}
                value={settings.category}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <option value="">Select category</option>
                {options.categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tags - WordPress and Wix */}
          {(isWordpress || isWix) && options.tags.length > 0 && (
            <div className={Styles.formGroup}>
              <label className={Styles.formLabel}>Tags</label>
              <div className={Styles.tagsContainer}>
                {options.tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    className={`${Styles.tagChip} ${settings.tags.includes(tag.id) ? Styles.tagSelected : ''}`}
                    onClick={() => onTagToggle(tag.id)}
                  >
                    {tag.name}
                    {settings.tags.includes(tag.id) && <FiCheck size={12} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Slug - All blog platforms */}
          <div className={Styles.formGroup}>
            <label className={Styles.formLabel}>URL Slug</label>
            <input
              type="text"
              className={Styles.formInput}
              value={settings.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              placeholder="my-blog-post-url"
            />
          </div>

          {/* Meta Description - WordPress and Wix */}
          {(isWordpress || isWix) && (
            <div className={Styles.formGroup}>
              <label className={Styles.formLabel}>Meta Description</label>
              <textarea
                className={Styles.formTextarea}
                value={settings.metaDescription}
                onChange={(e) => handleChange('metaDescription', e.target.value)}
                placeholder="Brief description for SEO..."
                rows={3}
              />
            </div>
          )}

          {/* Status */}
          <div className={Styles.formGroup}>
            <label className={Styles.formLabel}>Status</label>
            <select
              className={Styles.formSelect}
              value={settings.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <option value="publish">Publish</option>
              <option value="draft">Draft</option>
              {isWordpress && (
                <>
                  <option value="pending">Pending Review</option>
                  <option value="private_state">Private</option>
                </>
              )}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Platform logo mapping
const PLATFORM_LOGOS = {
  linkedin: { src: '/Images/linkedin.svg', alt: 'LinkedIn' },
  facebook: { src: '/Images/facebook.svg', alt: 'Facebook' },
  x: { src: '/Images/x.svg', alt: 'X' },
  twitter: { src: '/Images/x.svg', alt: 'X' },
  wordpress: { src: '/Images/wordpress.svg', alt: 'WordPress' },
  wix: { src: '/Images/wix.svg', alt: 'Wix' },
  webflow: { src: '/Images/webflow.svg', alt: 'Webflow' },
};

// Channel Card Component
const ChannelCard = ({ integration, isSelected, onClick }) => {
  const getChannelInfo = () => {
    if (integration.category === 'social') {
      // Get handle/username for social accounts
      const handle = integration.integration_data?.twitter_handle ||
                     integration.integration_data?.screen_name ||
                     integration.integration_data?.username ||
                     integration.integration_data?.handle;

      if (integration.integration_data?.type === 'page') {
        return {
          name: integration.integration_data?.page_name || integration.name,
          subtitle: handle ? `@${handle}` : `${integration.type.charAt(0).toUpperCase() + integration.type.slice(1)} Page`,
          followers: integration.integration_data?.followers_count || null
        };
      }
      return {
        name: `${integration.integration_data?.first_name || ''} ${integration.integration_data?.last_name || ''}`.trim() || integration.name,
        subtitle: handle ? `@${handle}` : 'Personal Account',
        followers: integration.integration_data?.followers_count || null
      };
    }
    // For website platforms (WordPress, Wix, Webflow)
    return {
      name: integration.name || integration.integration_data?.site_name || integration.type,
      subtitle: integration.type.charAt(0).toUpperCase() + integration.type.slice(1),
      followers: null
    };
  };

  const info = getChannelInfo();
  const initials = info.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const platformLogo = PLATFORM_LOGOS[integration.type?.toLowerCase()];

  return (
    <div
      className={`${Styles.channelCard} ${isSelected ? Styles.selected : ''}`}
      onClick={onClick}
    >
      <div className={Styles.channelAvatar}>
        {platformLogo ? (
          <img src={platformLogo.src} alt={platformLogo.alt} className={Styles.platformIcon} />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      <div className={Styles.channelInfo}>
        <p className={Styles.channelName}>{info.name}</p>
        <p className={Styles.channelMeta}>
          {info.subtitle}
          {info.followers && (
            <>
              <span className={Styles.dot}></span>
              {info.followers} Followers
            </>
          )}
        </p>
      </div>
      {isSelected && (
        <div className={Styles.checkmark}>
          <FiCheck size={14} />
        </div>
      )}
    </div>
  );
};

// Helper functions
const generateTimeOptions = () => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  return times;
};

const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${minutes} ${suffix}`;
};

export default PublishSettingsModal;
